import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import { urlEndPoint } from "helpers/GeneralHelper";

import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    Input,
    Row,
} from "reactstrap";
import TableContainer from "components/Common/TableContainer";
import { submitAIAddonCallTemplateFilterData } from "model/AIAddonCallTemplateModel";
import { useAIAddonCallTemplateContext } from "libs/context/AIAddonCallTemplateContext";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const AIAddOnTemplate = () => {
    const { permissionData } = usePermissionContext() || {};
    const navigate = useNavigate();
    const {
        fetchAIAddonCallTemplateContextData,
        aiAddonCallTemplateContextData,
    } = useAIAddonCallTemplateContext() || {};

    const [searchQuery, setSearchQuery] = useState("");
    const [aiAddonCallTemplateData, setAIAddonCallTemplateData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableMetaData, setTableMetadata] = useState();
    const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);

    const filteredData = useMemo(() => {
        if (!searchQuery?.trim()) return aiAddonCallTemplateData;

        return aiAddonCallTemplateData?.filter((item) =>
            Object.values(item).some((val) =>
                String(val).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [aiAddonCallTemplateData]);

    const aiAddonCallTemplateActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.AIAddonCallTemplateAddAccess),
            canEdit: Boolean(permissionData?.AIAddonCallTemplateUpdateAccess),
        }),
        [permissionData]
    );

    const hasAnyAIAddonCallTemplateRowAction = useMemo(() => {
        const { canCreate, ...rowActions } = aiAddonCallTemplateActionPermissions;
        return Object.values(rowActions).some(Boolean);
    }, [aiAddonCallTemplateActionPermissions]);

    useEffect(() => {
        fetchAIAddonCallTemplateContextData();
        getAIAddonCallTemplateList();
    }, []);

    useEffect(() => {
        const valueReModified = aiAddonCallTemplateContextData?.table?.columns?.map((col) => {
            const field = col?.field;
            const normalizedField = String(field ?? "").trim().toLowerCase();
            const isStatusColumn =
                normalizedField === "status" ||
                normalizedField === "statusid" ||
                String(col?.type ?? "").trim().toLowerCase() === "status";

            return {
                Header: col?.title,
                accessor: isStatusColumn ? "statusId" : field,
                type: col?.type,
            };
        });
        setTableMetadata(valueReModified);
    }, [aiAddonCallTemplateContextData]);

    const getAIAddonCallTemplateList = async (payload = {}) => {
        setLoading(true);
        const response = await submitAIAddonCallTemplateFilterData(payload);
        const responseData = response?.data || {};
        const listData = responseData?.records || responseData || [];

        if (response) {
            setAIAddonCallTemplateData(Array.isArray(listData) ? listData : []);
        } else {
            setAIAddonCallTemplateData([]);
        }
        setLoading(false);
    };

    const handleFilterSubmit = async (data) => {
        await getAIAddonCallTemplateList(data);
        setCommonFilterModalOpen(false);
    };

    const handleSearchSubmit = async (value) => {
        await getAIAddonCallTemplateList({ nameContains: value?.trim() || "" });
    };

    const handleEdit = (row) => {
        const templateId = row?.id ?? row?.Id;
        if (!templateId) return;
        const templateName = row?.name ?? row?.Name ?? row?.templateName ?? "";
        navigate(`/${urlEndPoint.addEditAIAddonCallTemplate}/${templateId}`, {
            state: { templateName },
        });
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">AI Add On Call Template</h5>
                                {aiAddonCallTemplateActionPermissions.canCreate ? (
                                    <Col xs="12" md="4" className="text-md-end mt-2 mt-md-0">
                                        <Button
                                            color="primary"
                                            onClick={() =>
                                                navigate(`/${urlEndPoint.addEditAIAddonCallTemplate}`)
                                            }
                                        >
                                            + New Template
                                        </Button>
                                    </Col>
                                ) : null}
                            </div>
                            <hr />

                            <Row className="align-items-center g-2 mb-4">
                                <Col xs="12" md="8">
                                    <div className="d-flex border rounded w-100 category-search-input">
                                        <Input
                                            type="text"
                                            placeholder="Search by name"
                                            className="border-0"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e?.target?.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    handleSearchSubmit(searchQuery?.trim());
                                                }
                                            }}
                                        />
                                        <Button
                                            className="search-icon-button"
                                            onClick={() => handleSearchSubmit(searchQuery?.trim())}
                                        >
                                            <i className="fa fa-search"></i>
                                        </Button>
                                    </div>
                                </Col>
                            </Row>

                            {!loading && tableMetaData?.length ? (
                                <TableContainer
                                    columns={tableMetaData}
                                    data={filteredData}
                                    isActionRequired={hasAnyAIAddonCallTemplateRowAction}
                                    isEditAccess={aiAddonCallTemplateActionPermissions.canEdit}
                                    onEdit={
                                        aiAddonCallTemplateActionPermissions.canEdit
                                            ? handleEdit
                                            : undefined
                                    }
                                    isCheckboxLeftSide={false}
                                    isPagination={false}
                                    setCommonFilterModalOpen={setCommonFilterModalOpen}
                                    pageName={"AIAddonCallTemplate"}
                                />
                            ) : (
                                <Loaders />
                            )}
                        </CardBody>
                    </Card>

                    <AllCommonFilterModal
                        setCommonFilterModalOpen={setCommonFilterModalOpen}
                        commonFilterModalOpen={commonFilterModalOpen}
                        modalTitle="AI Add On Call Template"
                        handleSubmit={handleFilterSubmit}
                        filterData={aiAddonCallTemplateContextData?.filters}
                    />
                </Container>
            </div>
        </React.Fragment>
    );
};

AIAddOnTemplate.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(AIAddOnTemplate);
